/**
 * パフォーマンス計測ユーティリティ
 *
 * ?debug=perf でアクセスするとコンソールに計測情報を出力する。
 *
 * - perfCount(): 呼び出し回数のカウント（ドラッグ書き込み等）
 * - onPerfRender(): React <Profiler> の onRender コールバック（render回数+所要時間）
 * - family集約 + hot instances top 20 で出力
 */
import { isPerfDebugModeEnabled } from './perfDebugMode';

type CounterName =
  | 'RTDB.write'
  | 'RTDB.write.throttled'
  | 'RTDB.write.final'
  | 'ElementDragManager.onDrag'
  | 'DragManager.drag'
  | 'ViewDrag.handleDragViews'
  | 'DragWriteBuffer.schedule'
  | 'DragWriteBuffer.timer-fired'
  | 'DragWriteBuffer.commit'
  | 'DragWriteBuffer.flush';

// --- カウンタ ---
const counters: Record<string, number> = {};
const snapshotCounters: Record<string, number> = {};

// --- Profiler 集計 ---
type ProfilerStats = {
  renderCount: number;
  totalActualDuration: number;
  totalBaseDuration: number;
  maxActualDuration: number;
};
const profilerStats: Record<string, ProfilerStats> = {};
const snapshotProfilerStats: Record<string, ProfilerStats> = {};

let intervalId: number | null = null;
let autoFlushTimerId: number | null = null;
let globalsInstalled = false;

type DisplayRow = {
  renders: number;
  'actual(ms)': number;
  'base(ms)': number;
  'max(ms)': number;
  'avg(ms)': number;
};

type PerfSnapshot = {
  counters: Record<string, number>;
  families: Record<string, DisplayRow>;
  hotInstances: Record<string, DisplayRow>;
};

type PerfSession = {
  label?: string;
  counters: Record<string, number>;
  profilerStats: Record<string, ProfilerStats>;
};

let activeSession: PerfSession | null = null;

/**
 * カウンタをインクリメントする
 */
// ts-unused-exports:disable-next-line
export function perfCount(name: CounterName): void {
  if (!isPerfDebugModeEnabled()) return;

  const names = name.startsWith('RTDB.write.') ? (['RTDB.write', name] as const) : ([name] as const);

  for (const counterMap of [counters, snapshotCounters, activeSession?.counters]) {
    if (!counterMap) continue;
    for (const counterName of names) {
      counterMap[counterName] = (counterMap[counterName] ?? 0) + 1;
    }
  }
}

/**
 * React <Profiler> の onRender コールバック。
 * PerfProfiler コンポーネント経由で使う。
 */
export function onPerfRender(
  id: string,
  _phase: 'mount' | 'update' | 'nested-update',
  actualDuration: number,
  baseDuration: number
): void {
  if (!isPerfDebugModeEnabled()) return;

  if (!profilerStats[id]) {
    profilerStats[id] = {
      renderCount: 0,
      totalActualDuration: 0,
      totalBaseDuration: 0,
      maxActualDuration: 0,
    };
  }
  if (!snapshotProfilerStats[id]) {
    snapshotProfilerStats[id] = {
      renderCount: 0,
      totalActualDuration: 0,
      totalBaseDuration: 0,
      maxActualDuration: 0,
    };
  }
  if (activeSession && !activeSession.profilerStats[id]) {
    activeSession.profilerStats[id] = {
      renderCount: 0,
      totalActualDuration: 0,
      totalBaseDuration: 0,
      maxActualDuration: 0,
    };
  }

  for (const stats of [profilerStats[id], snapshotProfilerStats[id], activeSession?.profilerStats[id]]) {
    if (!stats) continue;
    stats.renderCount += 1;
    stats.totalActualDuration += actualDuration;
    stats.totalBaseDuration += baseDuration;
    stats.maxActualDuration = Math.max(stats.maxActualDuration, actualDuration);
  }
}

// --- family 集約 ---

function getProfilerFamily(id: string): string {
  if (id.startsWith('page:')) return 'page';
  if (id.includes(':nodes+links')) return 'view:nodes+links';
  if (id.includes(':nodes')) return 'view:nodes';
  if (id.includes(':links')) return 'view:links';
  if (id.includes(':zones')) return 'view:zones';
  if (id.includes(':model')) return 'view:model';
  if (id.startsWith('view:')) return 'view';
  return 'other';
}

function toDisplayRow(stats: ProfilerStats): DisplayRow {
  return {
    renders: stats.renderCount,
    'actual(ms)': Math.round(stats.totalActualDuration * 10) / 10,
    'base(ms)': Math.round(stats.totalBaseDuration * 10) / 10,
    'max(ms)': Math.round(stats.maxActualDuration * 10) / 10,
    'avg(ms)': stats.renderCount > 0 ? Math.round((stats.totalActualDuration / stats.renderCount) * 10) / 10 : 0,
  };
}

function buildSnapshotFromStats(
  counterStats: Record<string, number>,
  profilerStatsMap: Record<string, ProfilerStats>
): PerfSnapshot {
  // counters
  const familyAcc: Record<string, ProfilerStats> = {};
  const instanceRows: [string, DisplayRow][] = [];

  for (const [id, stats] of Object.entries(profilerStatsMap)) {
    if (stats.renderCount === 0) continue;

    instanceRows.push([id, toDisplayRow(stats)]);

    const family = getProfilerFamily(id);
    const acc = (familyAcc[family] ??= {
      renderCount: 0,
      totalActualDuration: 0,
      totalBaseDuration: 0,
      maxActualDuration: 0,
    });
    acc.renderCount += stats.renderCount;
    acc.totalActualDuration += stats.totalActualDuration;
    acc.totalBaseDuration += stats.totalBaseDuration;
    acc.maxActualDuration = Math.max(acc.maxActualDuration, stats.maxActualDuration);
  }

  const families = Object.fromEntries(
    Object.entries(familyAcc).map(([family, stats]) => [family, toDisplayRow(stats)])
  );

  // hot instances: actual(ms) 降順 top 20
  const hotInstances = Object.fromEntries(
    instanceRows.sort(([, a], [, b]) => b['actual(ms)'] - a['actual(ms)']).slice(0, 20)
  );

  return { counters: { ...counterStats }, families, hotInstances };
}

function flushAll(): PerfSnapshot {
  return flushStats(counters, profilerStats);
}

function flushStats(
  counterStats: Record<string, number>,
  profilerStatsMap: Record<string, ProfilerStats>
): PerfSnapshot {
  const snapshot = buildSnapshotFromStats(counterStats, profilerStatsMap);

  // 次回 flush でログが 0 埋めの key で汚染されないよう key ごと削除する。
  // profilerStatsMap は id が view:${view.id} のように動的なので、残すと増殖する側面もある。
  for (const key of Object.keys(counterStats)) {
    delete counterStats[key];
  }
  for (const key of Object.keys(profilerStatsMap)) {
    delete profilerStatsMap[key];
  }

  return snapshot;
}

function printSnapshot(title: string, snapshot: PerfSnapshot): PerfSnapshot {
  const hasFamilies = Object.keys(snapshot.families).length > 0;
  const hasCounters = Object.keys(snapshot.counters).length > 0;

  if (!hasFamilies && !hasCounters) {
    // eslint-disable-next-line no-console
    console.log(`${title} no activity`);
    return snapshot;
  }

  if (hasFamilies) {
    // eslint-disable-next-line no-console
    console.log(`${title} Family Summary:`);
    // eslint-disable-next-line no-console
    console.table(snapshot.families);

    if (Object.keys(snapshot.hotInstances).length > 0) {
      // eslint-disable-next-line no-console
      console.log(`${title} Hot Instances (top 20):`);
      // eslint-disable-next-line no-console
      console.table(snapshot.hotInstances);
    }
  }
  if (hasCounters) {
    // eslint-disable-next-line no-console
    console.log(`${title} Counters:`);
    // eslint-disable-next-line no-console
    console.table(snapshot.counters);
  }

  return snapshot;
}

function formatLabel(label?: string): string {
  return label ? ` [${label}]` : '';
}

function clearAutoFlushTimer(): void {
  if (autoFlushTimerId === null) return;

  clearTimeout(autoFlushTimerId);
  autoFlushTimerId = null;
}

function scheduleAutoFlush(label?: string): void {
  if (!label?.startsWith('clear-before-')) return;

  clearAutoFlushTimer();

  const afterLabel = label.replace(/^clear-before-/, 'after-');

  // eslint-disable-next-line no-console
  console.log('[PerfMonitor] これから5秒計測します。3秒くらいドラッグして止めてください。');
  // eslint-disable-next-line no-console
  console.log(`[PerfMonitor] 5秒後に自動で Snapshot flush [${afterLabel}] を出します。`);

  autoFlushTimerId = window.setTimeout(() => {
    autoFlushTimerId = null;

    // eslint-disable-next-line no-console
    console.log('[PerfMonitor] ===== ここからコピー =====');
    perfFlush(afterLabel);
    // eslint-disable-next-line no-console
    console.log('[PerfMonitor] ===== ここまでコピー =====');
  }, 5000);
}

function perfStart(label?: string): void {
  if (!isPerfDebugModeEnabled()) return;

  activeSession = {
    label,
    counters: {},
    profilerStats: {},
  };

  // eslint-disable-next-line no-console
  console.log(`[PerfMonitor] Snapshot start${formatLabel(label)}`);
}

function perfStop(label?: string): PerfSnapshot {
  if (!isPerfDebugModeEnabled()) {
    return { counters: {}, families: {}, hotInstances: {} };
  }

  const session = activeSession;
  const actualLabel = label ?? session?.label;
  const snapshot = session
    ? flushStats(session.counters, session.profilerStats)
    : { counters: {}, families: {}, hotInstances: {} };

  activeSession = null;

  return printSnapshot(`[PerfMonitor] Snapshot stop${formatLabel(actualLabel)}`, snapshot);
}

function perfFlush(label?: string): PerfSnapshot {
  if (!isPerfDebugModeEnabled()) {
    return { counters: {}, families: {}, hotInstances: {} };
  }

  const snapshot = flushStats(snapshotCounters, snapshotProfilerStats);

  scheduleAutoFlush(label);

  return printSnapshot(`[PerfMonitor] Snapshot flush${formatLabel(label)}`, snapshot);
}

function installGlobals(): void {
  if (globalsInstalled || !isPerfDebugModeEnabled()) return;

  window.__perfStart = perfStart;
  window.__perfStop = perfStop;
  window.__perfFlush = perfFlush;
  globalsInstalled = true;
}

/**
 * 計測ログの定期出力を開始する（3秒ごと）
 */
export function startPerfMonitor(): void {
  if (!isPerfDebugModeEnabled()) return;
  if (intervalId !== null) return;

  installGlobals();

  // React.Profiler は production build では無効化される仕様のため onRender が呼ばれず
  // render 統計が一切集計されない。現状 perfCount の呼び出しもないので counter も空で、
  // 3 秒 interval がひたすら "no activity" を出し続けるノイズ源になる。警告だけ出して
  // interval は起動しない。
  if (process.env.NODE_ENV === 'production') {
    console.warn(
      '[PerfMonitor] ?debug=perf は production build では React.Profiler が無効化されるため ' +
        'render 統計を収集できません。計測するには dev build (yarn start-with-cloud 等) を使ってください。'
    );
    return;
  }

  // eslint-disable-next-line no-console
  console.log('[PerfMonitor] 計測開始。3秒ごとに出力します。');
  // eslint-disable-next-line no-console
  console.log(
    '[PerfMonitor] window.__perfStart(label?), window.__perfStop(label?), window.__perfFlush(label?) が使えます。'
  );

  intervalId = window.setInterval(() => {
    printSnapshot('[PerfMonitor] Auto', flushAll());
  }, 3000);
}

/**
 * 計測ログの出力を停止する
 *
 * activeSession と stats map もすべてクリアする。__perfStop() 忘れで画面を離脱しても
 * 次回 mount に持ち越さないようにするため。
 */
export function stopPerfMonitor(): void {
  clearAutoFlushTimer();
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  activeSession = null;
  for (const key of Object.keys(counters)) delete counters[key];
  for (const key of Object.keys(snapshotCounters)) delete snapshotCounters[key];
  for (const key of Object.keys(profilerStats)) delete profilerStats[key];
  for (const key of Object.keys(snapshotProfilerStats)) delete snapshotProfilerStats[key];
}

declare global {
  interface Window {
    __perfStart?: (label?: string) => void;
    __perfStop?: (label?: string) => PerfSnapshot;
    __perfFlush?: (label?: string) => PerfSnapshot;
  }
}
